import { CircularProgressWrapper } from '@montugroup/circuit-shared/components/common/CircularProgressWrapper';

import { FormControlLabel, InputLabel, Stack, styled, useMediaQuery, useTheme } from '@mui/material';

import { useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';

import { BrandedForm } from '@/components/forms/BrandedForms';
import settings from '@/constants/constants';
import { useGetPatientConsentQuestions } from '@/hooks/patient/useGetPatientConsentQuestions';
import type { PatientConsentFormData } from '@/services/patientConsent.service';
import Button from '@montugroup/circuit-shared/components/common/IconButton';
import { PatientConsentQuestions } from './PatientConsentQuestions';

import AlternaleafLogo from '../../layout/alternaleaf.svg';

import { getFullName } from '@/utils';
import useUser from '@montugroup/circuit-shared/hooks/user/useUser';
import { Box, Checkbox, Dialog, DialogContent, TextField, Typography } from '@montugroup/design-system';
import { UpperLeftDecoration } from './decoration';

const DecoratedContainer = styled(Box)`
  padding-top: 3rem;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const TitleRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginBottom: '1rem',
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}));

const ScrollContainer = styled(Box)`
  border-radius: 1rem;
  background-color: #f7f5f7;
  padding: 1rem;
  max-height: 15rem;
  overflow-y: auto;
  overflow-x: hidden;
  margin-bottom: 1rem;
`;

const ConsentCheckBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(8),
}));

const ErrorDisplay = styled(Box)(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'flex',
    width: '75vw',
    height: '75vh',
  },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  '&:not(:disabled)': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  textTransform: 'capitalize',
  fontSize: '1rem',
  lineHeight: 1.25,
  borderColor: 'transparent',
  borderWidth: 0,
  borderRadius: '6px',
  padding: '0.75rem 2rem',
  cursor: 'pointer',
  width: '100%',
  [theme.breakpoints.up('md')]: {
    width: 'auto',
  },
}));

const StyledInputLabel = styled(InputLabel)(({ theme }) => ({
  '&&': {
    marginLeft: 0,
    marginBottom: theme.spacing(1),
  },
}));

const StyledInputContainer = styled(Box)(({ theme }) => ({
  display: 'block',
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up('md')]: {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(5),
  },
}));

const StyledFormInput = styled(TextField)(({ theme }) => ({
  width: '100%',
  marginLeft: 0,
  [theme.breakpoints.up('md')]: {
    width: 'auto',
  },
}));

const RoundedDialog = styled(Dialog)`
  & .MuiPaper-root {
    border-radius: 2rem;
    margin: 0;
    padding: 0;
  }
`;

interface PatientConsentDialogProps {
  isOpen: boolean;
  onSubmit: (data: PatientConsentFormData) => void;
  requireReload?: boolean;
}

export const PatientConsentDialog = (props: PatientConsentDialogProps) => {
  const { isOpen, onSubmit, requireReload } = props;
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [shouldDisplayError, setShouldDisplayError] = useState<boolean>(false);
  const theme = useTheme();
  const isLargeView = useMediaQuery(theme.breakpoints.up('md'));

  const { register, handleSubmit, formState } = useForm<PatientConsentFormData>();
  const { errors, isValid } = formState;

  const patientConsent = useGetPatientConsentQuestions();
  const {
    data: patientConsentData,
    error: fetchConsentQuestionsError,
    isLoading: loadingConsentQuestions,
  } = patientConsent;
  const consentQuestions = patientConsentData?.body ?? null;

  const { data: user } = useUser();

  const fullName = getFullName(user?.firstName, user?.lastName);

  const submitHandler: SubmitHandler<PatientConsentFormData> = async (data) => {
    try {
      setIsSubmitting(true);
      await onSubmit(data);
      // Dialog will automatically close after a successful submission.
    } catch {
      setShouldDisplayError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasError = Boolean(fetchConsentQuestionsError) || requireReload || shouldDisplayError;

  return (
    <RoundedDialog open={isOpen} maxWidth="lg" fullScreen={!isLargeView} fullWidth={!isLargeView} scroll="paper">
      <UpperLeftDecoration />
      {!hasError && (
        <DialogContent>
          <DecoratedContainer>
            <div style={{ marginBottom: '1rem' }}>
              <img src={AlternaleafLogo} alt="Alternaleaf" style={{ height: '1rem' }} />
            </div>
            <TitleRow>
              <Typography component="h2" variant="h4" sx={(theme) => ({ color: theme.palette.primary.main })}>
                Treatment consent form
              </Typography>
              <Typography variant="body2" sx={(theme) => ({ color: theme.palette.secondary.main })}>
                All fields are mandatory *
              </Typography>
            </TitleRow>
            <div style={{ marginBottom: theme.spacing(4) }}>
              <Typography variant="body2">
                Our records show you have not completed the required medical consent form, kindly complete the below
                form to continue using our services.
              </Typography>
            </div>
            <div>
              <ScrollContainer>
                {loadingConsentQuestions ? (
                  <CircularProgressWrapper />
                ) : (
                  <PatientConsentQuestions questions={consentQuestions} />
                )}
              </ScrollContainer>
            </div>
            <BrandedForm action="" onSubmit={handleSubmit(submitHandler)}>
              <Stack
                sx={{
                  flexDirection: {
                    xs: 'column',
                    md: 'row',
                  },
                }}
              >
                <StyledInputContainer>
                  <StyledInputLabel htmlFor="firstName">First Name *</StyledInputLabel>
                  <StyledFormInput
                    id="firstName"
                    {...register('firstName', {
                      required: 'Please enter your first name',
                    })}
                    error={!!errors?.firstName?.message}
                    helperText={errors.firstName?.message ? errors.firstName.message : ''}
                    disabled={isSubmitting}
                  />
                </StyledInputContainer>
                <StyledInputContainer>
                  <StyledInputLabel htmlFor="lastName">Last Name *</StyledInputLabel>
                  <StyledFormInput
                    {...register('lastName', {
                      required: 'Please enter your last name',
                    })}
                    error={!!errors?.lastName?.message}
                    helperText={errors.lastName?.message ? errors.lastName.message : ''}
                    disabled={isSubmitting}
                  />
                </StyledInputContainer>
              </Stack>
              <ConsentCheckBox>
                <FormControlLabel
                  sx={{
                    alignItems: 'flex-start',
                  }}
                  control={
                    <Checkbox
                      {...register('consentGiven', { required: 'Required' })}
                      color="default"
                      sx={{
                        marginRight: '1rem',
                      }}
                    />
                  }
                  label={
                    <Box>
                      I, {fullName}, acknowledge that I have read and understood the information provided above
                      regarding the use of medicinal cannabis for my treatment. I have had the opportunity to ask
                      questions and discuss concerns with my Alternaleaf Pty LTD Clinicians.
                    </Box>
                  }
                />
              </ConsentCheckBox>
              <div>
                <SubmitButton type="submit" text="Submit" loading={isSubmitting} disabled={!isValid} />
              </div>
            </BrandedForm>
          </DecoratedContainer>
        </DialogContent>
      )}
      {hasError && (
        <DialogContent>
          <DecoratedContainer>
            <div style={{ marginBottom: '1rem' }}>
              <img src={AlternaleafLogo} alt="Alternaleaf" style={{ height: '1rem' }} />
            </div>
            <TitleRow>
              <Typography variant="h5" component="h2">
                Treatment consent form
              </Typography>
            </TitleRow>
            <ErrorDisplay>
              <Typography variant="body1">
                An error occurred. Please reload the page and try again. <br />
                If you&apos;re still experiencing issues please call {settings.support.phone.display} for support.
              </Typography>
            </ErrorDisplay>
          </DecoratedContainer>
        </DialogContent>
      )}
    </RoundedDialog>
  );
};
